import { format, subDays } from "date-fns";
import { FileSpreadsheet, FileText } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { SingleValue } from "react-select";

import { SelectInput } from "components/Select";
import SingleDatePicker from "components/SingleDatePicker";
import { PageLayout, PageTitle } from "components/layout";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { useGetStoresOptionsQuery } from "@/services/store/select-options";
import {
  useExportCashMovementExcelMutation,
  useExportCashMovementPdfMutation,
  useGetCashMovementAllowedStoresQuery,
} from "@/services/cash-movement";
import { layoutClasses } from "@/utils/css-classes";
import { formatOptionLabel } from "@/utils/format-option-label";

import type {
  CashMovementExportResult,
  ICashMovementExportParams,
  ICashMovementExportQueued,
} from "src/@types/cash-movement";

import {
  CASH_MOVEMENT_LABELS,
  CASH_MOVEMENT_MESSAGES,
  EMAIL_PLACEHOLDER,
  EXPORT_DATE_FORMAT,
  GLOBALLY_HANDLED_STATUS,
  type ExportKind,
  type StoreOption,
  type StoreOptions,
} from "./types";

const readValidationMessage = (error: unknown): string | undefined => {
  const message = (error as { data?: { message?: string | string[] } })?.data
    ?.message;

  if (Array.isArray(message)) return message[0];
  return message;
};

export default function CashMovementListingPage() {
  const [storeCode, setStoreCode] = useState<number | undefined>(undefined);
  const [date, setDate] = useState<Date | null>(null);
  const [operatorCode, setOperatorCode] = useState<number | undefined>(
    undefined,
  );

  const { data: storeOptions } = useGetStoresOptionsQuery(undefined, {
    refetchOnMountOrArgChange: false,
  });
  const { data: allowedStores } = useGetCashMovementAllowedStoresQuery();

  const allowedCodes: number[] | null = allowedStores?.storeCodes ?? null;

  const lastClosedDay: Date = useMemo(() => subDays(new Date(), 1), []);

  const availableStores: StoreOptions = useMemo(() => {
    const all: StoreOptions = storeOptions ?? [];
    if (allowedCodes === null) return all;
    return all.filter(option => allowedCodes.includes(Number(option.value)));
  }, [storeOptions, allowedCodes]);

  const isStoreLocked: boolean =
    allowedCodes !== null && availableStores.length === 1;

  useEffect(() => {
    if (isStoreLocked && storeCode === undefined) {
      setStoreCode(Number(availableStores[0].value));
    }
  }, [isStoreLocked, storeCode, availableStores]);

  const [exportExcel, { isLoading: exportingExcel }] =
    useExportCashMovementExcelMutation();
  const [exportPdf, { isLoading: exportingPdf }] =
    useExportCashMovementPdfMutation();

  const hasFilters: boolean = storeCode !== undefined && date !== null;
  const isExporting: boolean = exportingExcel || exportingPdf;

  const handleExport = async (kind: ExportKind): Promise<void> => {
    if (storeCode === undefined || date === null) {
      toast(CASH_MOVEMENT_MESSAGES.MISSING_FILTERS);
      return;
    }

    const formattedDate: string = format(date, EXPORT_DATE_FORMAT);

    try {
      const params: ICashMovementExportParams = {
        storeCode,
        date: formattedDate,
        operatorCode,
      };
      const result: CashMovementExportResult =
        kind === "excel"
          ? await exportExcel(params).unwrap()
          : await exportPdf(params).unwrap();
      const queued = result as ICashMovementExportQueued;

      if (queued?.jobId === undefined) {
        toast(CASH_MOVEMENT_MESSAGES.EMPTY);
        return;
      }

      toast({
        description: CASH_MOVEMENT_MESSAGES.EXPORT_QUEUED.description.replace(
          EMAIL_PLACEHOLDER,
          queued.email,
        ),
      });
    } catch (error) {
      const status = (error as { status?: number | string })?.status;
      if (GLOBALLY_HANDLED_STATUS.has(status as number)) return;

      const apiMessage: string | undefined = readValidationMessage(error);
      toast(
        apiMessage
          ? { ...CASH_MOVEMENT_MESSAGES.ERROR, description: apiMessage }
          : CASH_MOVEMENT_MESSAGES.ERROR,
      );
    }
  };

  const currentStoreOption: StoreOption | null =
    availableStores.find(option => Number(option.value) === storeCode) ?? null;

  const handleStoreChange = (option: SingleValue<StoreOption>): void => {
    const parsed: number = Number(option?.value);
    setStoreCode(option?.value && !Number.isNaN(parsed) ? parsed : undefined);
  };

  const handleOperatorChange = (value: string): void => {
    const parsed: number = Number(value);
    setOperatorCode(value !== "" && !Number.isNaN(parsed) ? parsed : undefined);
  };

  return (
    <PageLayout>
      <div className={layoutClasses.pageContainer}>
        <PageTitle
          title={CASH_MOVEMENT_LABELS.pageTitle}
          description={CASH_MOVEMENT_LABELS.pageDescription}
          titleSize="text-3xl"
          descriptionSize="text-xl"
        />

        <div className="flex flex-col lg:flex-row lg:items-end gap-4 mt-4">
          <div className="w-64">
            <label className="text-xs text-slate-500 mb-1 block">
              {CASH_MOVEMENT_LABELS.storeLabel}
            </label>
            <SelectInput
              options={availableStores}
              value={currentStoreOption}
              onChange={handleStoreChange}
              isClearable={!isStoreLocked}
              isDisabled={isStoreLocked}
              placeholder={CASH_MOVEMENT_LABELS.storePlaceholder}
              formatOptionLabel={formatOptionLabel}
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs text-slate-500 mb-1 block">
              {CASH_MOVEMENT_LABELS.dateLabel}
            </label>
            <SingleDatePicker
              date={date}
              onDateChange={setDate}
              maxDate={lastClosedDay}
              placeholder={CASH_MOVEMENT_LABELS.datePlaceholder}
              buttonClassName="w-64 h-8 min-h-[32px] rounded-md border border-solid border-[#d1d5db] bg-white text-slate-900 hover:bg-white text-sm font-normal"
            />
          </div>

          <div className="w-40">
            <label className="text-xs text-slate-500 mb-1 block">
              {CASH_MOVEMENT_LABELS.operatorLabel}
            </label>
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              value={operatorCode ?? ""}
              onChange={event => handleOperatorChange(event.target.value)}
              placeholder={CASH_MOVEMENT_LABELS.operatorPlaceholder}
              className="h-8 text-sm"
            />
          </div>

          <div className="flex gap-3">
            <Button
              onClick={() => handleExport("excel")}
              disabled={!hasFilters || isExporting}
              className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              {exportingExcel
                ? CASH_MOVEMENT_LABELS.generating
                : CASH_MOVEMENT_LABELS.excelButton}
            </Button>
            <Button
              onClick={() => handleExport("pdf")}
              disabled={!hasFilters || isExporting}
              className="h-8 bg-rose-600 hover:bg-rose-700 text-white"
            >
              <FileText className="w-4 h-4 mr-2" />
              {exportingPdf
                ? CASH_MOVEMENT_LABELS.generating
                : CASH_MOVEMENT_LABELS.pdfButton}
            </Button>
          </div>
        </div>

        {!hasFilters && (
          <p className="text-xs text-slate-500 mt-2">
            {CASH_MOVEMENT_LABELS.selectFiltersHint}
          </p>
        )}
      </div>
    </PageLayout>
  );
}
